import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelect, type MultiSelectOption } from "@tellescope/ui";
import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";

export interface CalendarTemplateFilterMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  className?: string;
}

/**
 * Debounced searchable multi-select for the calendar filter's "Template" value. Searches
 * appointment templates by title, and additionally resolves the labels of the currently-selected
 * ids (via `getByIds`) so selected templates stay labeled even when they aren't in the current
 * search results — `MultiSelect` derives its badges from `options`.
 */
export const CalendarTemplateFilterMultiSelect = ({
  value,
  onChange,
  className,
}: CalendarTemplateFilterMultiSelectProps) => {
  const { t } = useTranslation("contacts");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 400);

  const templatesQuery = useTellescopeInfiniteQuery({
    model: "calendar_event_templates",
    method: "getSome",
    request: { search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined },
    select: (data) => data.pages.flat(),
  });

  const selectedTemplatesQuery = useTellescopeQuery({
    model: "calendar_event_templates",
    method: "getByIds",
    request: { ids: value },
    enabled: value.length > 0,
  });

  const options = useMemo<MultiSelectOption[]>(() => {
    const byId = [...(selectedTemplatesQuery.data?.matches ?? []), ...(templatesQuery.data ?? [])].reduce(
      (acc, template) => acc.set(template.id, template.title),
      new Map<string, string>()
    );
    const withFallbacks = value.reduce((acc, id) => (acc.has(id) ? acc : acc.set(id, id)), byId);
    return [...withFallbacks].map(([id, label]) => ({ value: id, label }));
  }, [templatesQuery.data, selectedTemplatesQuery.data, value]);

  return (
    <MultiSelect
      className={className}
      minPopoverWidth={240}
      options={options}
      value={value}
      onValueChange={onChange}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      isLoading={templatesQuery.isLoading || searchQuery !== debouncedSearchQuery}
      placeholder={t("Select template")}
      searchPlaceholder={t("Search...")}
      emptyText={t("No templates found")}
    />
  );
};
